import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { useOnline, useOutboxCounts } from '../offline/index.js';
import { isNative } from '../config.js';
import { api, ApiError } from '../api/client.js';
import { onPushState, pushState } from '../native/push.js';
import { useAppUpdate, versionText } from '../native/updates.js';
import UpdateButton from '../components/UpdateButton.jsx';
import { OFFICE_PHONE, OFFICE_TEL } from '../contact.js';
import { MyTickets } from '../modules/helpdesk/Helpdesk.jsx';

// Field support: the quickest way to a person, helpdesk tickets (logged with
// or without signal; the app's details go with them), and what the office
// needs to help (app version, connection, anything stuck in the outbox).
// Live chat comes later.

const PUSH_WORDS = {
  on: 'On',
  unknown: 'Starting…',
  blocked: 'Off: allow notifications for Nano Utilities in your phone settings',
  error: 'Not working on this phone',
  unavailable: isNative ? 'Not in this version of the app' : 'Only in the phone app',
};

export default function Support() {
  const online = useOnline();
  const { pending, rejected } = useOutboxCounts();
  const [push, setPush] = useState(pushState);
  const [test, setTest] = useState(null);
  const update = useAppUpdate();

  useEffect(() => onPushState(setPush), []);

  const sendTest = async () => {
    setTest({ busy: true });
    try {
      const r = await api('/push/test', { method: 'POST' });
      setTest({ text: r.sent ? 'Sent. It should arrive in a few seconds.' : 'The server has no phone registered for you yet. Close and reopen the app, then try again.' });
    } catch (e) {
      setTest({ text: e?.code === 'offline' ? 'No connection.' : e instanceof ApiError ? e.message : 'Could not send. Try again.' });
    }
  };

  return (
    <div className="stack">
      <section className="card">
        <h1>Support</h1>
        <p>Stuck on a job or something in the app not working? Call the office.</p>
        <div className="actions">
          <a className="btn primary" href={OFFICE_TEL}>Call the office · {OFFICE_PHONE}</a>
        </div>
      </section>

      <section className="card">
        <div className="page-head">
          <h2>Helpdesk tickets</h2>
          <Link to="/helpdesk/new" className="btn primary with-icon"><Plus size={18} aria-hidden="true" /> Log a ticket</Link>
        </div>
        <p className="muted small">App not working, need access to something, or another IT problem? Log a ticket and the helpdesk replies here.</p>
        <MyTickets limit={5} />
      </section>

      <section className="card">
        <h2>Your app</h2>
        <dl className="facts">
          <dt>Version</dt><dd>{versionText(update.native)}{update.ready ? ' (an update is ready: it starts next time you open the app)' : ''}</dd>
          <dt>Connection</dt><dd>{online ? 'Online' : 'Offline'}</dd>
          <dt>Waiting to send</dt><dd>{pending}</dd>
          <dt>Need attention</dt><dd>{rejected}</dd>
          <dt>Notifications</dt><dd>{PUSH_WORDS[push]}</dd>
        </dl>
        <p className="muted small">Read these out if the office asks.</p>
        <div className="actions">
          <UpdateButton />
        </div>
        {push === 'on' && (
          <div className="actions">
            <button type="button" className="btn" disabled={test?.busy || !online} onClick={sendTest}>
              {test?.busy ? 'Sending…' : 'Send a test notification'}
            </button>
          </div>
        )}
        {test?.text && <p className="small" role="status">{test.text}</p>}
      </section>
    </div>
  );
}
